import 'dayjs/locale/es';
import { useEffect } from 'react';
import { Grid } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import AutocompleteComponent from '../../../../components/layout/Autocomplete';
import useTranslate from '../../../../hooks/useTranslate';
import { useRoles } from '../../../../hooks/useRoles';
import { useAppDispatch } from '../../../../hooks/reduxHooks';
import { setSelectedOrganizationId } from '../../../../redux/slices/organizationsSlice';
import { useOrganizationFilter } from '../../../../contexts/OrganizationFilterContext';
import useOrganizationSelector from '../../../../components/layout/OrganizationSelector/hook/useOrganizationSelector';
import { TEST_IDS } from '../../consts/testIds';

interface VisitsFiltersProps {
    extraFilters: any;
    handleChangeExtraFilters: (e: any) => void;
    contractsOptions: any;
    isFetchingContractsOptions: any;
    establishmentsOptions: any;
    isFetchingEstablishmentsOptions: any;
    setFilters: any;
    handleDateChange: any;
    visitTypesOptions: { id: number; nombre: string; value: number }[];
    isFetchingVisitTypes: boolean;
}

const VisitsFilters = (props: VisitsFiltersProps) => {
    const {
        extraFilters,
        contractsOptions,
        isFetchingContractsOptions,
        establishmentsOptions,
        isFetchingEstablishmentsOptions,
        handleChangeExtraFilters,
        visitTypesOptions,
        isFetchingVisitTypes
    } = props;
    const { t } = useTranslation('filesGenerationSRT');
    const { translate } = useTranslate(t);

    const dispatch = useAppDispatch();

    const { isPerfilBackOffice } = useRoles();
    const { selectedOrganization, setSelectedOrganization } = useOrganizationFilter();
    const { organizations: organizationsOptions } = useOrganizationSelector();

    // Sincronizar el valor del contexto con el filtro local
    useEffect(() => {        
        if (selectedOrganization && extraFilters?.idOrganizacion !== selectedOrganization) {
            handleChangeExtraFilters({
                target: {
                    name: 'idOrganizacion',
                    value: selectedOrganization
                }
            });
            dispatch(setSelectedOrganizationId(selectedOrganization));
        }
    }, [selectedOrganization, extraFilters?.idOrganizacion, handleChangeExtraFilters]);

    const handleOrganizationChange = (value: any) => {
        setSelectedOrganization(value);
        handleChangeExtraFilters({
            target: {
                name: 'idOrganizacion',
                value: value || ''
            }
        });
    };
    
    return (
        <>
            {isPerfilBackOffice && (
                <Grid item xs={2.5}>
                    <AutocompleteComponent
                        value={selectedOrganization || extraFilters?.idOrganizacion || null}
                        options={organizationsOptions || []}
                        size="small"
                        name="idOrganizacion"
                        loading={false}
                        label={t('header.extraFilters.organization')}
                        templateLabel={(option: any) => {
                            return `${option?.nombre}`;
                        }}
                        onChange={handleOrganizationChange}
                        clearable
                    />
                </Grid>
            )}
            <Grid item xs={isPerfilBackOffice ? 2.5 : 3}>
                <AutocompleteComponent
                    label={`${translate('header.extraFilters.contract')}`}
                    size="small"
                    name="idContrato"
                    disabled={isFetchingContractsOptions}
                    loading={isFetchingContractsOptions}
                    onChange={(value: any) => {
                        handleChangeExtraFilters({
                            target: {
                                name: 'idContrato',
                                value: value
                            }
                        });
                    }}
                    templateLabel={(item: any) =>
                        `${item?.cuit || ''} ${item?.cuit && '-'} ${item?.nombre || ''}`
                    }
                    options={contractsOptions || []}
                    value={extraFilters?.idContrato || ''}
                    testID={TEST_IDS.extraFilters.visits.contract}
                    clearable
                />
            </Grid>
            <Grid item xs={isPerfilBackOffice ? 2.5 : 3}>
                <AutocompleteComponent
                    label={`${translate('header.extraFilters.establishment')}`}
                    size="small"
                    name="idEstablecimiento"
                    disabled={isFetchingEstablishmentsOptions}
                    loading={isFetchingEstablishmentsOptions}
                    onChange={(value: any) => {
                        handleChangeExtraFilters({
                            target: {
                                name: 'idEstablecimiento',
                                value: value
                            }
                        });
                    }}
                    templateLabel={(item: any) =>
                        `${item?.numero || ''} ${item?.numero && '-'} ${item?.nombre || ''}`
                    }
                    options={establishmentsOptions || []}
                    value={extraFilters?.idEstablecimiento || ''}
                    testID={TEST_IDS.extraFilters.visits.establishment}
                    clearable
                />
            </Grid>
            <Grid item xs={isPerfilBackOffice ? 2.5 : 3}>
                <AutocompleteComponent
                    label={`${translate('header.extraFilters.visitType')}`}
                    size="small"
                    name="idTipo"
                    disabled={isFetchingVisitTypes}
                    loading={isFetchingVisitTypes}
                    onChange={(value: any) => {
                        handleChangeExtraFilters({
                            target: {
                                name: 'idTipo',
                                value: value
                            }
                        });
                    }}
                    options={visitTypesOptions}
                    value={extraFilters?.idTipo || null}
                    testID={TEST_IDS.extraFilters.visits.visitType}
                    clearable
                />
            </Grid>
            <Grid item xs={isPerfilBackOffice ? 2.5 : 3}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                    <DatePicker
                        slotProps={{
                            textField: {
                                inputProps: { 'data-testid': 'visits-filter-visit-date' }
                            }
                        }}
                        sx={{display: 'flex'}}
                        label="Fecha de Visita"
                        value={extraFilters?.fecha}
                        name="fecha"
                        onChange={(value: any) => {
                            handleChangeExtraFilters({
                                target: {
                                    name: 'fecha',
                                    value: value
                                }
                            });
                        }}
                        disableFuture={true}
                        format="DD/MM/YYYY"
                    />
                </LocalizationProvider>
            </Grid>
        </>
    );
};

export { VisitsFilters };
